import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import useLanguage from "../../utils/useLanguage";
import { DataContext } from "../../utils/DataContext";

import classes from "../../components/Modal.module.css";
import "../InvoicesManagement.scss";

import SectionDivider from "../../components/SectionDivider";
import CustomInput from "../../components/CustomInput";
import CustomButton from "../../components/CustomButton";
import AddAnotherButton, { GENDER } from "../../components/AddAnotherButton";


import CustomInputWithUnit from "../../components/CustomInputWithUnit";
import { toMoney } from "../../utils/toMoney";
import { sanitizeNumber } from "../../utils/sanitizeNumber";
import { onlyNums } from "../../utils/OnlyNums";

export default function ItemInvoiceCard({ sendDataToParent, data, error }) {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");
  // const { error } = useContext(DataContext);

  // items
  const [items, setItems] = useState([
    {
      name: "",
      width: "",
      height: "",
      quantity: 0,
      price: 0,
      totalPrice: 0,
    },
  ]);

  const [page, setPage] = useState(1);

  const spanStyles = currentDirection === "rtl" ? classes.shortSpan : null;
  // const error = error === "emptyInputFields" ? "emptyInputField" : "";

  const calcTotalPrice = (price, quantity) => {
    return (
      toMoney(parseFloat(sanitizeNumber(price)) * parseFloat(quantity)) || 0
    );
  };

  const handleInputChange = (e, pageIndex) => {
    const { name, value } = e.target;

    setItems((prev) =>
      prev.map((item, index) => {
        // Keep other items unchanged
        if (index !== pageIndex) return item;

        // Update the target item
        const updatedItem = { ...item, [name]: value };

        // When Price || quantity updated, re-calculate the totalPrice
        return {
          ...updatedItem,
          totalPrice: calcTotalPrice(
            name === "price" ? value : updatedItem.price,
            name === "quantity" ? value : updatedItem.quantity
          ),
        };
      })
    );
  };

  const handleAddInput = (e) => {
    e.preventDefault();

    const newItem = {
      name: "",
      width: "",
      height: "",
      quantity: 0,
      price: 0,
      totalPrice: 0,
    };

    setItems((prevItems) => [...prevItems, newItem]);

    // Ensure we navigate to the newly added item
    setPage((prevPage) => prevPage + 1);
  };

  const handleNext = (e) => {
    e.preventDefault();

    if (page < items.length) setPage(page + 1);
  };

  const handlePrevious = (e) => {
    e.preventDefault();

    if (page > 1) setPage(page - 1);
  };

  useEffect(() => {
    if (data && Array.isArray(data) && data.length > 0) {
      setItems(data);
    }
  }, [data]);

  useEffect(() => {
    const modifiedItems = items?.map((item) => ({
      ...item,
      price: parseFloat(item?.price),
      totalPrice: parseFloat(sanitizeNumber(item?.totalPrice)),
      quantity: parseInt(item?.quantity),
    }));

    sendDataToParent(modifiedItems);
  }, [items]);

  return (
    <>
      <SectionDivider title="itemsInfo" />

      <div
        className={`add-shipped-container-container-part ${classes.flexableForm} ${classes.flexableFormWithPadding}`}
      >
        {items?.map((item, index) => {
          if (index + 1 === page) {
            return (
              <>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("itemName")}: </span>
                  <CustomInput
                    name="name"
                    value={item?.name || ""}
                    onChange={(e) => handleInputChange(e, index)}
                    flex={true}
                    errorText={error}
                  />
                </div>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("price")}: </span>
                  <CustomInputWithUnit
                    name="price"
                    value={item?.price || ""}
                    onChange={(e) => handleInputChange(e, index)}
                    onKeyDown={onlyNums}
                    flex={true}
                    unit="IQD"
                    errorText={error}
                  />
                </div>

                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("numberOfBoxes")}: </span>
                  <CustomInputWithUnit
                    name="quantity"
                    value={item?.quantity || ""}
                    onChange={(e) => handleInputChange(e, index)}
                    flex={true}
                    onKeyDown={onlyNums}
                    errorText={error}
                    unit={t("m")}
                  />
                </div>

                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("width")}: </span>
                  <CustomInput
                    name="width"
                    value={item?.width || ""}
                    onChange={(e) => handleInputChange(e, index)}
                    flex={true}
                  />
                </div>

                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("length")}: </span>
                  <CustomInput
                    name="height"
                    value={item?.height || ""}
                    onChange={(e) => handleInputChange(e, index)}
                    flex={true}
                  />
                </div>
                <div className={classes.inputContainer}>
                  <span className={spanStyles}>{t("totalPrice")}: </span>
                  <div className="u-width_full-width">
                    {item.totalPrice} {t("iqd")}
                  </div>
                </div>
              </>
            );
          }
          return null;
        })}
      </div>

      <div>
        <div className={""}>
          {items?.length ? (
            <div className={"add-shipped-container-next-previous"}>
              <CustomButton
                variant="blank"
                title={t("previous")}
                onClick={handlePrevious}
                disabled={page === 1}
                icon={currentDirection === "ltr" ? "back" : "next"}
                iconSize={15}
              />
              <CustomButton
                title={t("next")}
                variant="blank"
                onClick={handleNext}
                disabled={page === items.length}
                // disabled={page === mainState.bankAccounts.length}
                icon={currentDirection === "ltr" ? "next" : "back"}
                iconSize={15}
                reverseIcon
              />
            </div>
          ) : null}
        </div>
        <div className={"add-shipped-container-add-another"}>
          <AddAnotherButton
            onClick={handleAddInput}
            variant="blank"
            title={t("addAnotherItem")}
            icon="add"
            iconSize={16}
            gender={GENDER.FEMALE}
            arrayLength={items?.length}
          />
        </div>
      </div>
    </>
  );
}
